import { apiClient } from './api';

export interface CustomerAddress {
  id: string;
  customer_id: string;
  address_type: 'billing' | 'shipping' | 'both';
  is_default: boolean;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  contact_person?: string;
  contact_phone?: string;
  gstin?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export interface CustomerAddressCreate {
  address_type: 'billing' | 'shipping' | 'both';
  is_default?: boolean;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  contact_person?: string;
  contact_phone?: string;
  gstin?: string;
}

export interface CustomerAddressUpdate extends Partial<CustomerAddressCreate> {}

export interface CustomerAddressListResponse {
  addresses: CustomerAddress[];
  total_count: number;
  billing_addresses: number;
  shipping_addresses: number;
  both_addresses: number;
}

export class CustomerAddressService {
  private baseUrl = '/admin/customers';

  async getCustomerAddresses(
    customerId: string,
    params?: {
      address_type?: 'billing' | 'shipping' | 'both';
      include_inactive?: boolean;
    }
  ): Promise<CustomerAddressListResponse> {
    const response = await apiClient.get(`${this.baseUrl}/${customerId}/addresses`, { params });
    return response.data;
  }

  async getCustomerAddress(customerId: string, addressId: string): Promise<CustomerAddress> {
    const response = await apiClient.get(`${this.baseUrl}/${customerId}/addresses/${addressId}`);
    return response.data;
  }

  async createCustomerAddress(customerId: string, data: CustomerAddressCreate): Promise<CustomerAddress> {
    const response = await apiClient.post(`${this.baseUrl}/${customerId}/addresses`, data);
    return response.data;
  }

  async updateCustomerAddress(
    customerId: string,
    addressId: string,
    data: CustomerAddressUpdate
  ): Promise<CustomerAddress> {
    const response = await apiClient.put(`${this.baseUrl}/${customerId}/addresses/${addressId}`, data);
    return response.data;
  }

  async setDefaultAddress(
    customerId: string,
    addressId: string,
    data: { address_type: 'billing' | 'shipping' }
  ): Promise<CustomerAddress> {
    const response = await apiClient.post(`${this.baseUrl}/${customerId}/addresses/${addressId}/set-default`, data);
    return response.data;
  }

  async deactivateCustomerAddress(customerId: string, addressId: string): Promise<CustomerAddress> {
    const response = await apiClient.delete(`${this.baseUrl}/${customerId}/addresses/${addressId}`);
    return response.data;
  }

  async getCustomerAddressHistory(
    customerId: string,
    params?: {
      address_id?: string;
      page?: number;
      per_page?: number;
    }
  ): Promise<{
    history: Array<{
      id: string;
      address_id: string;
      change_type: string;
      old_values?: any;
      new_values?: any;
      changed_by_admin_id: string;
      changed_at: string;
    }>;
    total: number;
    page: number;
    per_page: number;
  }> {
    const response = await apiClient.get(`${this.baseUrl}/${customerId}/addresses/history`, { params });
    return response.data;
  }
}

export const customerAddressService = new CustomerAddressService();